import { Formik } from 'formik';
import React, { useRef } from 'react';
import * as Yup from 'yup';
import { BusinessUnitSelect, Form, MemberSelect, PracticeSelect } from '~/components';
import { BulkActionEditControl, NO_CHANGE, CLEAR, SET, BulkActionModal } from '~/components/bulk-action';
import FixedDropdown from '~/components/FixedDropdown';
import { useApi, useWorkspace } from '~/contexts';
import { useFeatures } from '~/hooks';
import ContactDetails from './ContactDetails';

const validationSchema = Yup.object().shape({
  owner: Yup.object()
    .nullable()
    .when('ownerAction', { is: SET, then: (schema) => schema.required('Please select an owner.') }),
  practice: Yup.object()
    .nullable()
    .when('practiceAction', { is: SET, then: (schema) => schema.required('Please select a practice.') }),
  businessUnit: Yup.object()
    .nullable()
    .when('businessUnitAction', { is: SET, then: (schema) => schema.required('Please select a business unit.') }),
});

const validate = (values) => {
  const errors = {};

  const hasAtLeastOneChange =
    values.ownerAction !== NO_CHANGE || values.practiceAction !== NO_CHANGE || values.businessUnitAction !== NO_CHANGE;

  if (!hasAtLeastOneChange) {
    const errorMessage = 'At least one field is required.';
    errors.ownerAction = errorMessage;
    errors.practiceAction = errorMessage;
    errors.businessUnitAction = errorMessage;
  }

  return errors;
};

function BulkEditForm({ features }) {
  return (
    <Form style={{ paddingBottom: '2rem' }}>
      <p style={{ marginBottom: '1.5rem' }}>Select the fields that you would like to bulk update.</p>

      <BulkActionEditControl label="Owner" actionName="ownerAction" valueName="owner">
        {(props) => <MemberSelect {...props} placeholder="Owner" component={FixedDropdown} />}
      </BulkActionEditControl>

      {features.practices && (
        <BulkActionEditControl label="Practice" actionName="practiceAction" valueName="practice">
          {(props) => <PracticeSelect {...props} placeholder="Practice" allowNew={true} component={FixedDropdown} />}
        </BulkActionEditControl>
      )}

      {features.businessUnits && (
        <BulkActionEditControl label="Business Unit" actionName="businessUnitAction" valueName="businessUnit">
          {(props) => <BusinessUnitSelect {...props} placeholder="Business Unit" component={FixedDropdown} />}
        </BulkActionEditControl>
      )}
    </Form>
  );
}

export default function BulkEditContact({ ids, onClose, onComplete }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const features = useFeatures();
  const formRef = useRef();

  return (
    <BulkActionModal
      title="Bulk Edit"
      onClose={onClose}
      onComplete={onComplete}
      onConfirm={() => formRef.current?.submitForm()}
      renderRow={ContactDetails}
      width="48rem">
      <Formik
        innerRef={formRef}
        initialValues={{
          ownerAction: NO_CHANGE,
          owner: null,
          practiceAction: NO_CHANGE,
          practice: null,
          businessUnitAction: NO_CHANGE,
          businessUnit: null,
        }}
        onSubmit={async (values) => {
          const payload = {};

          const applyField = (key, action, value, map = (v) => v?.id) => {
            if (action === CLEAR) payload[key] = null;
            else if (action === SET && value != null) payload[key] = map(value);
          };

          applyField('ownerId', values.ownerAction, values.owner);
          applyField('practiceId', values.practiceAction, values.practice);
          applyField('businessUnitId', values.businessUnitAction, values.businessUnit);

          return api.www
            .workspaces(workspace.id)
            .contacts()
            .bulkEdit(ids, payload)
            .then((r) => r.data);
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema}
        validate={validate}>
        <BulkEditForm features={features} />
      </Formik>
    </BulkActionModal>
  );
}
